import React, { useCallback } from "react"
import Pagination, { type PaginationProps } from "@mui/material/Pagination"
import TablePagination, { type TablePaginationOwnProps } from "@mui/material/TablePagination"

interface Metadata {
  currentPage: number
  totalCount: number
  totalPages: number
}

interface KaminariPaginationPropsRegular extends PaginationProps {
  metadata: Metadata
  page: number
  onPageChange: (page: number) => void
}

interface KaminariPaginationPropsTable extends Omit<TablePaginationOwnProps, "onPageChange"> {
  metadata: Metadata
  page: number
  onPageChange: (page: number) => void
  perPage: number
  onPerPageChange: (perPage: number) => void
  rowsPerPageOptions?: number[]
}

type KaminariPaginationProps = KaminariPaginationPropsRegular | KaminariPaginationPropsTable

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100]

const KaminariPaginationRegular: React.FC<KaminariPaginationPropsRegular> = ({
  metadata,
  page = 1,
  onPageChange,
  ...props
}) => {
  const handleChange = useCallback(
    (_event: React.ChangeEvent<unknown>, page: number) => onPageChange(page),
    [onPageChange],
  )

  return (
    <Pagination
      count={metadata.totalPages}
      page={page}
      onChange={handleChange}
      data-testid="pagination-regular"
      {...props}
    />
  )
}

const KaminariPaginationTable: React.FC<KaminariPaginationPropsTable> = ({
  metadata,
  page = 1,
  onPageChange,
  perPage,
  onPerPageChange,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  ...props
}) => {
  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, page: number) => onPageChange(page + 1),
    [onPageChange],
  )

  const handlePerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (onPerPageChange) {
      onPerPageChange(Number(event.target.value))
    }
  }

  return (
    <TablePagination
      component="div"
      count={metadata.totalCount}
      page={page - 1}
      onPageChange={handlePageChange}
      rowsPerPage={perPage}
      onRowsPerPageChange={handlePerPageChange}
      rowsPerPageOptions={rowsPerPageOptions}
      data-testid="pagination-table"
      {...props}
    />
  )
}

const KaminariPagination: React.FC<KaminariPaginationProps> = (props) => {
  if ("perPage" in props) {
    return <KaminariPaginationTable {...props} />
  }

  return <KaminariPaginationRegular {...props} />
}

export default KaminariPagination
